import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useListState } from '@mantine/hooks';
import { BlockStyleDto, ContractTemplateDto, TextBlockDto } from '../../Types/DocumentT';
import { ContractTemplateS } from '../ContractTemplateS';
import { ContractS } from '../ContractS';
import { AgreementDTO } from '../DocumentT';

interface ContractTemplateContextProps {
  loading: boolean;
  movingBlock: boolean;
  template: ContractTemplateDto | null;
  aboveBlocks: TextBlockDto[];
  belowBlocks: TextBlockDto[];
  handleChange: (patch: Partial<ContractTemplateDto>) => void;
  savePatch: (patch: Partial<ContractTemplateDto>) => void;
  changeTextBlock: (blockId: number, index: number, isAbove: boolean, patch: Partial<TextBlockDto>) => Promise<boolean>;
  addBlock: (above: boolean, afterBlock?: number, textBlock?: Partial<TextBlockDto>) => Promise<boolean>;
  moveBlock: (from: number, to: number, fromBelow: boolean, toBelow: boolean) => void;
  deleteBlock: (index: number, isAbove?: boolean) => void;
  insertPositions: (blockId: number) => Promise<boolean>;
  removePositions: () => void;
  changeBlockStyle: (
    blockId: number,
    index: number,
    isAbove: boolean,
    prop: keyof TextBlockDto,
    patch: Partial<BlockStyleDto>,
  ) => void;
}

export const ContractTemplateContext = createContext<ContractTemplateContextProps>({} as ContractTemplateContextProps);

export const ContractTemplateContextProvider = () => {
  const { templateName } = useParams();
  const [template, setTemplate] = useState<ContractTemplateDto | null>(null);
  const [operationStatus, setStatus] = useState({
    loadingTemplate: false,
    movingBlock: false,
  });
  const [aboveBlocks, aboveHandlers] = useListState<TextBlockDto>([]);
  const [belowBlocks, belowHandlers] = useListState<TextBlockDto>([]);

  useEffect(() => {
    if (templateName) {
      setStatus((prev) => ({ ...prev, loadingTemplate: true }));
      ContractTemplateS.fetchTemplate(templateName)
        .then((resJSON) => {
          if (resJSON) {
            const { aboveTextBlocks, belowTextBlocks } = resJSON;
            aboveHandlers.setState(aboveTextBlocks);
            belowHandlers.setState(belowTextBlocks);
            setTemplate(resJSON);
          }
        })
        .finally(() => setStatus((prev) => ({ ...prev, loadingTemplate: false })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateName]);

  const savePatch = async (patch: Partial<ContractTemplateDto>) => {
    if (template) {
      const resJSON = await ContractTemplateS.editTemplate(template.id, patch);
      if (resJSON) {
        setTemplate(resJSON);
      }
    }
  };

  const handleChange = (patch: Partial<ContractTemplateDto>) => {
    setTemplate((prev) => {
      if (prev) {
        return { ...prev, ...patch };
      }
      return null;
    });
  };

  const changeTextBlock = async (blockId: number, index: number, isAbove: boolean, patch: Partial<TextBlockDto>) => {
    const resJSON = await ContractS.editTextBlock(blockId, patch);
    if (resJSON) {
      const { setItem } = isAbove ? aboveHandlers : belowHandlers;
      setItem(index, resJSON);
      return true;
    }
    return false;
  };

  const addBlock = async (above: boolean, afterBlock?: number, textBlock?: Partial<TextBlockDto>) => {
    if (templateName) {
      const { addBlockAbove, addBlockBelow } = ContractTemplateS;
      const handler = above ? addBlockAbove : addBlockBelow;
      const resJSON = await handler(templateName, afterBlock, textBlock);
      if (resJSON) {
        if (above) {
          aboveHandlers.setState(resJSON.aboveTextBlocks);
        } else {
          belowHandlers.setState(resJSON.belowTextBlocks);
        }
        return true;
      }
    }
    return false;
  };

  const moveBlock = async (from: number, to: number, fromBelow: boolean, toBelow: boolean) => {
    if (templateName) {
      setStatus((prev) => ({ ...prev, movingBlock: true }));
      const resJSON = await ContractTemplateS.moveBlock(templateName, from, to, fromBelow, toBelow);
      if (resJSON) {
        const { aboveTextBlocks, belowTextBlocks } = resJSON;
        aboveHandlers.setState(aboveTextBlocks);
        belowHandlers.setState(belowTextBlocks);
      }
      setStatus((prev) => ({ ...prev, movingBlock: false }));
    }
  };

  const deleteBlock = (index: number, isAbove = false) => {
    const { remove } = isAbove ? aboveHandlers : belowHandlers;
    remove(index);
  };

  const insertPositions = async (blockId: number) => {
    if (templateName) {
      const resJSON = await ContractTemplateS.insertPositions(templateName, blockId);
      if (resJSON) {
        const { aboveTextBlocks, belowTextBlocks } = resJSON;
        aboveHandlers.setState(aboveTextBlocks);
        belowHandlers.setState(belowTextBlocks);
        setTemplate(resJSON);
        return true;
      }
    }
    return false;
  };

  const removePositions = async () => {
    if (template) {
      const resJSON = await ContractTemplateS.editTemplate(template.id, { positions: null });
      if (resJSON) {
        const { aboveTextBlocks } = resJSON;
        aboveHandlers.setState(aboveTextBlocks);
        belowHandlers.setState([]);
        setTemplate(resJSON);
      }
    }
  };

  const changeBlockStyle = async (
    blockId: number,
    index: number,
    isAbove: boolean,
    prop: keyof TextBlockDto,
    patch: Partial<BlockStyleDto>,
  ) => {
    const resJSON = await ContractS.editBlockStyle(blockId, patch);
    if (resJSON) {
      const { setItemProp } = isAbove ? aboveHandlers : belowHandlers;
      setItemProp(index, prop, resJSON);
    }
  };

  const value = useMemo(
    () => ({
      loading: operationStatus.loadingTemplate,
      movingBlock: operationStatus.movingBlock,
      template,
      aboveBlocks,
      belowBlocks,
      handleChange,
      savePatch,
      changeTextBlock,
      addBlock,
      moveBlock,
      deleteBlock,
      insertPositions,
      removePositions,
      changeBlockStyle,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operationStatus, template, aboveBlocks, belowBlocks],
  );

  return (
    <ContractTemplateContext.Provider value={value}>
      <Outlet />
    </ContractTemplateContext.Provider>
  );
};

export const useAgreements = () => {
  const { template, handleChange } = useContext(ContractTemplateContext);
  const { agreements } = template ?? { agreements: [] };

  const append = (agreement: AgreementDTO) => {
    handleChange({ agreements: [...agreements, agreement] });
  };

  const remove = (agreementId: string) => {
    const tmp = agreements.filter((a) => a.id !== agreementId);
    handleChange({ agreements: tmp });
  };

  const patch = (agreement: AgreementDTO) => {
    const tmp = agreements.map((a) => (a.id !== agreement.id ? a : agreement));
    handleChange({ agreements: tmp });
  };

  return { agreements, append, remove, patch };
};
